import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { APP_ID, APP_NAME } from '../../brand';
import type { Reservation } from '../../core/model/reservation';
import { addressSingleLine } from '../../core/model/restaurant';
import { Formatters } from '../../core/util/formatters';
import { S } from '../../i18n/strings';
import { useContainer } from '../../state/ContainerContext';
import { useAsync } from '../../state/useAsync';
import { SkeletonBlock, StateContainer } from '../components/StateContainer';
import { CheckIcon } from '../components/icons';

/**
 * Confirmación de la reserva.
 *
 * Vuelve a pedir la reserva por su id en lugar de recibirla por estado de navegación: así un
 * enlace compartido o una recarga muestran exactamente lo mismo.
 */
export function ConfirmationScreen() {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { reservations } = useContainer();
  const wasModification = params.get('modificada') === 'true';

  const { state, reload } = useAsync(() => reservations.get(id), [reservations, id]);

  return (
    <StateContainer state={state} loading={<SkeletonBlock height={200} />} onRetry={reload}>
      {(reservation) => {
        const address = addressSingleLine(reservation.address);
        return (
          <div className="stack stack--loose">
            <div className="confirmation-hero">
              <CheckIcon size={40} />
              <h1>{wasModification ? S.confirmationTitleModified : S.confirmationTitle}</h1>
              <p className="confirmation-code">{S.confirmationCode(reservation.confirmationCode)}</p>
            </div>

            <div className="card" style={{ padding: 16 }}>
              <dl className="summary">
                <dt className="summary__key">Restaurante</dt>
                <dd className="summary__value">{reservation.restaurantName}</dd>
                <dt className="summary__key">{S.pickerDate}</dt>
                <dd className="summary__value">{Formatters.longDate(reservation.date)}</dd>
                <dt className="summary__key">{S.pickerTime}</dt>
                <dd className="summary__value">{Formatters.time(reservation.time)}</dd>
                <dt className="summary__key">{S.pickerParty}</dt>
                <dd className="summary__value">{Formatters.partySize(reservation.partySize)}</dd>
                {address && (
                  <>
                    <dt className="summary__key">Dirección</dt>
                    <dd className="summary__value">{address}</dd>
                  </>
                )}
              </dl>
            </div>

            <p className="muted">{S.bookingTermsBody}</p>

            <div className="row">
              <a className="btn btn--outline" href={calendarUrl(reservation)} download={`reserva-${reservation.confirmationCode}.ics`}>
                {S.confirmationAddCalendar}
              </a>
              <button type="button" className="btn btn--outline" onClick={() => navigate('/reservas')}>
                {S.confirmationViewReservations}
              </button>
              <span className="spacer" />
              {/* Vuelve al inicio reemplazando el histórico: retroceder no puede reabrir el
                  formulario ya enviado. */}
              <button type="button" className="btn" onClick={() => navigate('/', { replace: true })}>
                {S.confirmationDone}
              </button>
            </div>
          </div>
        );
      }}
    </StateContainer>
  );
}

/** Archivo .ics generado en el cliente: no hace falta servidor para llevarse la cita. */
function calendarUrl(reservation: Reservation): string {
  const start = `${reservation.date.replace(/-/g, '')}T${reservation.time.replace(':', '')}00`;
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    `PRODID:-//${APP_NAME}//ES`,
    'BEGIN:VEVENT',
    `UID:${reservation.confirmationCode}@${APP_ID}`,
    `DTSTART:${start}`,
    `SUMMARY:${reservation.restaurantName} (${reservation.partySize} personas)`,
    `DESCRIPTION:Código de confirmación ${reservation.confirmationCode}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ];
  return `data:text/calendar;charset=utf-8,${encodeURIComponent(lines.join('\r\n'))}`;
}
